/**
 * Helpers for working with the unified `attachedTools` array stored on
 * Skill and Agent draft/published snapshots:
 *
 *   attachedTools: Array<{ tool?: ObjectId, builtin?: string, usage?: string }>
 *
 * Each entry has exactly one of `tool` (DB Tool ObjectId) or `builtin` (named
 * built-in code tool). `usage` is optional creator-supplied guardrails text.
 */

export function attachmentKey(att) {
  if (!att) return '';
  if (att.tool) return `tool:${String(att.tool)}`;
  if (att.builtin) return `builtin:${att.builtin}`;
  return '';
}

export function attachedToolIdSet(attachments = []) {
  return new Set(attachments.filter(a => a?.tool).map(a => String(a.tool)));
}

export function attachedBuiltinNameSet(attachments = []) {
  return new Set(attachments.filter(a => a?.builtin).map(a => a.builtin));
}

export function isToolAttached(attachments, toolId) {
  return attachedToolIdSet(attachments).has(String(toolId));
}

export function isBuiltinAttached(attachments, builtin) {
  return attachedBuiltinNameSet(attachments).has(builtin);
}

export function findAttachment(attachments = [], { tool, builtin } = {}) {
  return attachments.find(a => {
    if (tool) return a.tool && String(a.tool) === String(tool);
    if (builtin) return a.builtin === builtin;
    return false;
  });
}

export function attachTool(attachments = [], toolId, usage = '') {
  if (isToolAttached(attachments, toolId)) return attachments;
  return [...attachments, { tool: toolId, usage }];
}

export function attachBuiltin(attachments = [], builtin, usage = '') {
  if (isBuiltinAttached(attachments, builtin)) return attachments;
  return [...attachments, { builtin, usage }];
}

export function detachTool(attachments = [], toolId) {
  return attachments.filter(a => !(a.tool && String(a.tool) === String(toolId)));
}

export function detachBuiltin(attachments = [], builtin) {
  return attachments.filter(a => a.builtin !== builtin);
}

export function setAttachmentUsage(attachments = [], { tool, builtin }, usage) {
  return attachments.map(a => {
    if (tool && a.tool && String(a.tool) === String(tool)) return { ...a, usage };
    if (builtin && a.builtin === builtin) return { ...a, usage };
    return a;
  });
}

export function sanitizeAttachmentsForApi(attachments = []) {
  return attachments
    .map(a => {
      if (a.tool) return { tool: String(a.tool), usage: a.usage || '' };
      if (a.builtin) return { builtin: a.builtin, usage: a.usage || '' };
      return null;
    })
    .filter(Boolean);
}
